import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CloudflareImage } from '@/components/common/CloudflareImage';
import { cn } from '@/lib/utils';
import { X, Maximize2, ChevronLeft, ChevronRight } from 'lucide-react';
import { AnimatePresence, motion, PanInfo } from 'framer-motion';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import type { ProductImage } from '@/types';

interface ProductGalleryProps {
  images: ProductImage[];
  className?: string;
}

export function ProductGallery({ images, className }: ProductGalleryProps) {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [direction, setDirection] = useState(0);

  // Reset to first image when images array changes
  useEffect(() => {
    setActiveImageIndex(0);
    setDirection(0);
  }, [images]);

  // Lock body scroll when lightbox is open (iOS Safari + Android Chrome compatible)
  useBodyScrollLock(isLightboxOpen);

  const paginate = useCallback((newDirection: number) => {
    setDirection(newDirection);
    setActiveImageIndex((prev) => {
      let next = prev + newDirection;
      if (next < 0) next = images.length - 1;
      if (next >= images.length) next = 0;
      return next;
    });
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') paginate(-1);
      else if (e.key === 'ArrowRight') paginate(1);
      else if (e.key === 'Escape') setIsLightboxOpen(false);
    };

    if (isLightboxOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, paginate]);

  const activeImage = images[activeImageIndex];

  // Drag handler for main gallery swipe support
  const onDragEnd = (_e: MouseEvent | TouchEvent | PointerEvent, { offset, velocity }: PanInfo) => {
    const swipe = Math.abs(offset.x) * velocity.x;
    if (swipe < -10000 || offset.x < -100) {
      paginate(1);
    } else if (swipe > 10000 || offset.x > 100) {
      paginate(-1);
    }
  };

  // Drag handler for lightbox swipe support
  const handleLightboxDragEnd = useCallback(
    (_e: MouseEvent | TouchEvent | PointerEvent, { offset, velocity }: PanInfo) => {
      const swipeThreshold = 50;
      const velocityThreshold = 500;
      const swipePower = Math.abs(offset.x) * Math.abs(velocity.x);

      if (offset.x < -swipeThreshold || (velocity.x < -velocityThreshold && swipePower > 10000)) {
        paginate(1);
      } else if (offset.x > swipeThreshold || (velocity.x > velocityThreshold && swipePower > 10000)) {
        paginate(-1);
      }
    },
    [paginate]
  );

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
      zIndex: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0
    })
  };

  const LightboxModal = () => {
    if (!isLightboxOpen) return null;

    return createPortal(
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-md flex items-center justify-center"
          onClick={() => setIsLightboxOpen(false)}
        >
          {/* Close Button */}
          <button
            className="absolute top-6 right-6 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors z-[10000]"
            onClick={() => setIsLightboxOpen(false)}
          >
            <X className="w-8 h-8" />
          </button>

          {/* Navigation */}
          {images.length > 1 && (
            <>
              <button
                className="absolute left-4 md:left-8 p-4 text-white hover:scale-110 transition-transform z-[10000]"
                onClick={(e) => { e.stopPropagation(); paginate(-1); }}
              >
                <ChevronLeft className="w-10 h-10 md:w-12 md:h-12" />
              </button>
              <button
                className="absolute right-4 md:right-8 p-4 text-white hover:scale-110 transition-transform z-[10000]"
                onClick={(e) => { e.stopPropagation(); paginate(1); }}
              >
                <ChevronRight className="w-10 h-10 md:w-12 md:h-12" />
              </button>
            </>
          )}

          {/* Image with Swipe Support */}
          <div
            className="relative w-full h-full p-4 md:p-12 flex items-center justify-center overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-lightbox-content
          >
            <AnimatePresence initial={false} custom={direction} mode="popLayout">
              <motion.div
                key={activeImageIndex}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ x: { type: "spring", stiffness: 300, damping: 30 }, opacity: { duration: 0.2 } }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.7}
                onDragEnd={handleLightboxDragEnd}
                className="absolute w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing"
              >
                <img
                  src={activeImage?.url}
                  alt={activeImage?.alt}
                  className="max-w-full max-h-full object-contain shadow-2xl rounded-sm select-none"
                  draggable={false}
                />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Counter */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 text-sm font-medium tracking-widest uppercase">
            {activeImageIndex + 1} / {images.length}
          </div>

        </motion.div>
      </AnimatePresence>,
      document.body
    );
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Main Swipeable Area */}
      <div className="relative aspect-[3/4] bg-white rounded-lg overflow-hidden group">
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.div
            key={activeImageIndex}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={onDragEnd}
            className="absolute inset-0 cursor-grab active:cursor-grabbing"
            onClick={() => setIsLightboxOpen(true)}
          >
            <CloudflareImage
              src={activeImage?.url || ''}
              alt={activeImage?.alt || 'Product Image'}
              className="w-full h-full"
              objectFit="cover"
              priority
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
          </motion.div>
        </AnimatePresence>

        {/* Hover Controls for Desktop */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-4 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); paginate(-1); }}
                className="bg-white/90 p-2 rounded-full shadow-lg hover:bg-white transition-colors pointer-events-auto"
              >
                <ChevronLeft className="w-5 h-5 text-charcoal" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); paginate(1); }}
                className="bg-white/90 p-2 rounded-full shadow-lg hover:bg-white transition-colors pointer-events-auto"
              >
                <ChevronRight className="w-5 h-5 text-charcoal" />
              </button>
            </>
          )}
        </div>

        {/* Zoom Trigger */}
        <button
          onClick={() => setIsLightboxOpen(true)}
          className="absolute top-4 right-4 bg-white/90 p-2.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm z-10"
        >
          <Maximize2 className="w-5 h-5 text-charcoal" />
        </button>
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide snap-x">
          {images.map((image, index) => (
            <button
              key={image.id}
              onClick={() => {
                setDirection(index > activeImageIndex ? 1 : -1);
                setActiveImageIndex(index);
              }}
              className={cn(
                "relative flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-all snap-start",
                activeImageIndex === index
                  ? "border-charcoal opacity-100 ring-1 ring-charcoal/20"
                  : "border-transparent opacity-60 hover:opacity-100"
              )}
            >
              <CloudflareImage
                src={image.url}
                alt={image.alt || 'Product thumbnail'}
                width={80}
                height={80}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      <LightboxModal />
    </div>
  );
}
